import { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { toast } from "sonner";

const TABS = ["Feature Cards", "Announcements", "Journey Tasks"];

const Toggle = ({ enabled, onChange, testid }) => (
  <button
    data-testid={testid}
    onClick={onChange}
    className={`w-11 h-6 rounded-full transition-colors duration-300 relative ${enabled ? "bg-[#D4AF37]" : "bg-white/10"}`}
  >
    <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all duration-300 ${enabled ? "left-6" : "left-1"}`} />
  </button>
);

const TextInput = (props) => (
  <input {...props} className="bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-slate-500 outline-none transition-colors duration-300 focus:border-[#D4AF37]/70" />
);

export default function AdminCMS() {
  const [tab, setTab] = useState(0);
  const [cards, setCards] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [newItem, setNewItem] = useState({});

  const load = useCallback(() => {
    api.get("/admin/cards").then((r) => setCards(r.data)).catch(() => {});
    api.get("/admin/announcements").then((r) => setAnnouncements(r.data)).catch(() => {});
    api.get("/admin/journey-tasks").then((r) => setTasks(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const patch = async (path, id, body) => {
    try {
      await api.patch(`/admin/${path}/${id}`, body);
      load();
    } catch {
      toast.error("Save failed");
    }
  };

  const remove = async (path, id) => {
    try {
      await api.delete(`/admin/${path}/${id}`);
      toast.success("Removed");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const create = async (path, body, reset) => {
    try {
      await api.post(`/admin/${path}`, body);
      toast.success("Created");
      setNewItem(reset);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    }
  };

  const reorder = (list, setPath, id, dir) => {
    const idx = list.findIndex((x) => x[setPath] === id);
    const swap = list[idx + dir];
    if (!swap) return;
    patch(setPath === "card_id" ? "cards" : "journey-tasks", id, { order: swap.order });
    patch(setPath === "card_id" ? "cards" : "journey-tasks", swap[setPath], { order: list[idx].order });
  };

  return (
    <AdminLayout title="Content Management">
      <div className="flex gap-2 mb-8 flex-wrap" data-testid="cms-tabs">
        {TABS.map((t, i) => (
          <button
            key={t}
            data-testid={`cms-tab-${t.toLowerCase().replace(/\s/g, "-")}`}
            onClick={() => setTab(i)}
            className={`rounded-full px-6 py-2.5 text-sm transition-colors duration-300 ${tab === i ? "bg-[#D4AF37] text-[#0A1633] font-semibold" : "border border-white/10 text-slate-300 hover:border-[#D4AF37]/40"}`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 0 && (
        <div data-testid="cms-cards">
          <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 mb-6 grid sm:grid-cols-4 gap-3">
            <TextInput data-testid="new-card-title" placeholder="Card title *" value={newItem.title || ""} onChange={(e) => setNewItem({ ...newItem, title: e.target.value })} />
            <TextInput data-testid="new-card-desc" placeholder="Description" value={newItem.description || ""} onChange={(e) => setNewItem({ ...newItem, description: e.target.value })} />
            <TextInput data-testid="new-card-icon" placeholder="Icon (e.g. Sparkles, Map)" value={newItem.icon || ""} onChange={(e) => setNewItem({ ...newItem, icon: e.target.value })} />
            <button
              data-testid="add-card-button"
              onClick={() => newItem.title?.trim() && create("cards", { title: newItem.title.trim(), description: newItem.description || "", icon: newItem.icon || "Sparkles", order: cards.length + 1 }, {})}
              className="flex items-center justify-center gap-2 rounded-xl bg-[#D4AF37] text-[#0A1633] font-semibold text-sm px-4 py-2.5 transition-colors duration-300 hover:bg-[#e5c558]"
            >
              <Plus className="w-4 h-4" /> Add card
            </button>
          </div>
          <div className="space-y-3">
            {cards.map((c, i) => (
              <div key={c.card_id} data-testid={`cms-card-row-${c.card_id}`}
                className={`rounded-2xl border border-white/10 bg-white/[0.02] px-6 py-4 flex items-center gap-4 flex-wrap ${!c.enabled ? "opacity-50" : ""}`}>
                <div className="flex flex-col">
                  <button data-testid={`card-up-${c.card_id}`} onClick={() => reorder(cards, "card_id", c.card_id, -1)} disabled={i === 0} className="text-slate-500 hover:text-[#D4AF37] disabled:opacity-20"><ArrowUp className="w-3.5 h-3.5" /></button>
                  <button data-testid={`card-down-${c.card_id}`} onClick={() => reorder(cards, "card_id", c.card_id, 1)} disabled={i === cards.length - 1} className="text-slate-500 hover:text-[#D4AF37] disabled:opacity-20"><ArrowDown className="w-3.5 h-3.5" /></button>
                </div>
                <TextInput value={c.title} data-testid={`card-title-${c.card_id}`} onChange={(e) => patch("cards", c.card_id, { title: e.target.value })} className="!w-48" />
                <TextInput value={c.description || ""} data-testid={`card-desc-${c.card_id}`} onChange={(e) => patch("cards", c.card_id, { description: e.target.value })} className="flex-1 min-w-[200px]" />
                <Toggle testid={`card-toggle-${c.card_id}`} enabled={c.enabled} onChange={() => patch("cards", c.card_id, { enabled: !c.enabled })} />
                <button data-testid={`card-delete-${c.card_id}`} onClick={() => remove("cards", c.card_id)} className="text-slate-500 transition-colors duration-300 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 1 && (
        <div data-testid="cms-announcements">
          <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 mb-6 grid sm:grid-cols-3 gap-3">
            <TextInput data-testid="new-ann-title" placeholder="Announcement title *" value={newItem.annTitle || ""} onChange={(e) => setNewItem({ ...newItem, annTitle: e.target.value })} />
            <TextInput data-testid="new-ann-body" placeholder="Body (optional)" value={newItem.annBody || ""} onChange={(e) => setNewItem({ ...newItem, annBody: e.target.value })} />
            <button
              data-testid="add-announcement-button"
              onClick={() => newItem.annTitle?.trim() && create("announcements", { title: newItem.annTitle.trim(), body: newItem.annBody || "" }, { annTitle: "", annBody: "" })}
              className="flex items-center justify-center gap-2 rounded-xl bg-[#D4AF37] text-[#0A1633] font-semibold text-sm px-4 py-2.5 transition-colors duration-300 hover:bg-[#e5c558]"
            >
              <Plus className="w-4 h-4" /> Publish
            </button>
          </div>
          <div className="space-y-3">
            {announcements.map((a) => (
              <div key={a.announcement_id} data-testid={`cms-ann-row-${a.announcement_id}`}
                className={`rounded-2xl border border-white/10 bg-white/[0.02] px-6 py-4 flex items-center gap-4 flex-wrap ${!a.enabled ? "opacity-50" : ""}`}>
                <TextInput value={a.title} data-testid={`ann-title-${a.announcement_id}`} onChange={(e) => patch("announcements", a.announcement_id, { title: e.target.value })} className="!w-64" />
                <TextInput value={a.body || ""} data-testid={`ann-body-${a.announcement_id}`} onChange={(e) => patch("announcements", a.announcement_id, { body: e.target.value })} className="flex-1 min-w-[200px]" />
                <Toggle testid={`ann-toggle-${a.announcement_id}`} enabled={a.enabled} onChange={() => patch("announcements", a.announcement_id, { enabled: !a.enabled })} />
                <button data-testid={`ann-delete-${a.announcement_id}`} onClick={() => remove("announcements", a.announcement_id)} className="text-slate-500 transition-colors duration-300 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 2 && (
        <div data-testid="cms-tasks">
          <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 mb-6 flex gap-3 flex-wrap">
            <TextInput data-testid="new-task-title" placeholder="New journey task *" value={newItem.taskTitle || ""} onChange={(e) => setNewItem({ ...newItem, taskTitle: e.target.value })} className="flex-1 min-w-[220px]" />
            <button
              data-testid="add-task-button"
              onClick={() => newItem.taskTitle?.trim() && create("journey-tasks", { title: newItem.taskTitle.trim(), order: tasks.length + 1 }, { taskTitle: "" })}
              className="flex items-center justify-center gap-2 rounded-xl bg-[#D4AF37] text-[#0A1633] font-semibold text-sm px-6 py-2.5 transition-colors duration-300 hover:bg-[#e5c558]"
            >
              <Plus className="w-4 h-4" /> Add task
            </button>
          </div>
          <div className="space-y-3">
            {tasks.map((t, i) => (
              <div key={t.task_id} data-testid={`cms-task-row-${t.task_id}`}
                className={`rounded-2xl border border-white/10 bg-white/[0.02] px-6 py-4 flex items-center gap-4 ${!t.enabled ? "opacity-50" : ""}`}>
                <div className="flex flex-col">
                  <button data-testid={`task-up-${t.task_id}`} onClick={() => reorder(tasks, "task_id", t.task_id, -1)} disabled={i === 0} className="text-slate-500 hover:text-[#D4AF37] disabled:opacity-20"><ArrowUp className="w-3.5 h-3.5" /></button>
                  <button data-testid={`task-down-${t.task_id}`} onClick={() => reorder(tasks, "task_id", t.task_id, 1)} disabled={i === tasks.length - 1} className="text-slate-500 hover:text-[#D4AF37] disabled:opacity-20"><ArrowDown className="w-3.5 h-3.5" /></button>
                </div>
                <TextInput value={t.title} data-testid={`task-title-${t.task_id}`} onChange={(e) => patch("journey-tasks", t.task_id, { title: e.target.value })} className="flex-1" />
                <Toggle testid={`task-toggle-${t.task_id}`} enabled={t.enabled} onChange={() => patch("journey-tasks", t.task_id, { enabled: !t.enabled })} />
                <button data-testid={`task-delete-${t.task_id}`} onClick={() => remove("journey-tasks", t.task_id)} className="text-slate-500 transition-colors duration-300 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
